import { useCallback } from 'react'

import { saveAs } from 'file-saver'
import mixpanel from 'mixpanel-browser'
import ReactTooltip from 'react-tooltip'
import styled from 'styled-components'

import icon3DDark from '../../../../../../../assets/images/icon_3d_dark.svg'
import icon3DLight from '../../../../../../../assets/images/icon_3d_light.svg'
import iconDownload from '../../../../../../../assets/images/icon_download.svg'
import iconShare from '../../../../../../../assets/images/icon_share.svg'
import iconSplitDark from '../../../../../../../assets/images/icon_split_dark.svg'
import iconSplit from '../../../../../../../assets/images/icon_vertical_split.svg'
import iconZoomIn from '../../../../../../../assets/images/icon_zoom_in.svg'
import iconZoomOut from '../../../../../../../assets/images/icon_zoom_out.svg'
import { useBuildingContext } from '../../../../../../../contexts/buildingContext'
import { useNotifications } from '../../../../../../../contexts/notificationProvider'
import { useViewportWidth } from '../../../../../../../hooks/useViewportWidth'
import { BREAKPOINTS, media } from '../../../../../../../styles/breakpoints'
import { IconButton } from '../../../../../../common/IconButton'
import { useImageViewerContext } from '../../../../imageViewerContext'
import { ViewerPosition } from '../../../../types'

interface ImageViewerSettingsProps {
  viewerPosition: ViewerPosition
  zoomLevel: number
  zoomMax: number
  zoomMin: number
  zoomStep: number
  onZoomChanged(level: number): void
  onTakeScreenshot(callback: (blob: Blob) => void): void
}

const MobileToolbar = styled.div`
  display: none;

  @media ${media.belowTablet} {
    display: flex;
    position: fixed;
    right: 16px;
    left: 16px;
    bottom: max(16px, env(safe-area-inset-bottom, 16px));
    justify-content: center;
    gap: 12px;
    z-index: 3;
  }
`

export const ImageViewerSettings = ({
  viewerPosition,
  zoomLevel,
  zoomMax,
  zoomMin,
  zoomStep,
  onZoomChanged,
  onTakeScreenshot,
}: ImageViewerSettingsProps) => {
  const { addNotification } = useNotifications()
  const { state: ImageViewerState, updateImageViewer } = useImageViewerContext()
  const { state: BuildingState } = useBuildingContext()
  const viewportWidth = useViewportWidth()
  const isMobileLayout = viewportWidth < BREAKPOINTS.tablet

  const handleDownloadClick = useCallback(() => {
    onTakeScreenshot((blob: Blob) => {
      saveAs(blob, 'screen.png')
    })
  }, [onTakeScreenshot])

  const copyUrl = () => {
    const qpObj = {
      yaw: viewerPosition.yaw.toFixed(3),
      pitch: viewerPosition.pitch.toFixed(3),
      hfov: viewerPosition.hfov.toFixed(3),
    }
    const qp = new URLSearchParams(qpObj)
    mixpanel.track('Share Url')
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(
        window.location.href.split('?')[0] + '?' + qp,
      )
    } else {
      const textArea = document.createElement('textarea')
      textArea.value = window.location.href.split('?')[0] + '?' + qp
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      textArea.style.top = '-999999px'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()
      return new Promise<void>((res, rej) => {
        document.execCommand('copy') ? res() : rej()
        textArea.remove()
      })
    }
  }

  const handleZoomIn = () => {
    if (zoomLevel > zoomMin) {
      onZoomChanged(Math.max(zoomLevel - zoomStep, zoomMin))
    }
  }

  const handleZoomOut = () => {
    if (zoomLevel < zoomMax) {
      onZoomChanged(Math.min(zoomLevel + zoomStep, zoomMax))
    }
  }

  return (
    <div style={{ position: 'fixed', zIndex: 3 }}>
      {!isMobileLayout && (
        <>
          <IconButton
            icon={iconZoomIn}
            size={40}
            primary={true}
            disabled={zoomLevel == zoomMin}
            onClick={handleZoomIn}
            style={{
              position: 'fixed',
              right: '40px',
              top: 'calc(50% - 22px)',
            }}
          />
          <IconButton
            icon={iconZoomOut}
            size={40}
            primary={true}
            disabled={zoomLevel == zoomMax}
            onClick={handleZoomOut}
            style={{
              position: 'fixed',
              right: '40px',
              top: 'calc(50% + 22px)',
            }}
          />
        </>
      )}

      {!isMobileLayout && !ImageViewerState.splitScreen ? (
        <>
          <IconButton
            icon={iconDownload}
            size={60}
            primary={false}
            onClick={handleDownloadClick}
            style={{ position: 'fixed', right: '40px', bottom: '100px' }}
            tooltip="Screenshot"
          />
          <IconButton
            onClick={() => {
              copyUrl().then(() =>
                addNotification('URL copied to clipboard', 'success'),
              )
            }}
            icon={iconShare}
            size={60}
            primary={false}
            style={{ position: 'fixed', right: '108px', bottom: '100px' }}
            tooltip="Share Url"
          />
          <IconButton
            onClick={() => {
              updateImageViewer({ splitScreen: !ImageViewerState.splitScreen })
            }}
            icon={ImageViewerState.splitScreen ? iconSplit : iconSplitDark}
            size={60}
            primary={ImageViewerState.splitScreen}
            style={{ position: 'fixed', right: '176px', bottom: '100px' }}
            tooltip="Split Viewer"
          />
        </>
      ) : !isMobileLayout ? (
        <>
          <IconButton
            onClick={() => {
              updateImageViewer({ splitScreen: !ImageViewerState.splitScreen })
            }}
            icon={ImageViewerState.splitScreen ? iconSplit : iconSplitDark}
            size={60}
            primary={ImageViewerState.splitScreen}
            style={{ position: 'fixed', right: '40px', bottom: '100px' }}
            tooltip="Split Viewer"
          />
          {BuildingState.projectData.forge_model_urn && (
            <IconButton
              onClick={() => {
                updateImageViewer({
                  pane2Type:
                    ImageViewerState.pane2Type === 'forge' ? 'image' : 'forge',
                })
              }}
              icon={
                ImageViewerState.pane2Type === 'forge'
                  ? icon3DLight
                  : icon3DDark
              }
              size={60}
              primary={ImageViewerState.pane2Type === 'forge'}
              style={{ position: 'fixed', right: '108px', bottom: '100px' }}
              tooltip="3D Model"
            />
          )}
        </>
      ) : null}

      {isMobileLayout && (
        <MobileToolbar>
          <IconButton
            icon={iconZoomIn}
            size={48}
            primary={true}
            disabled={zoomLevel == zoomMin}
            onClick={handleZoomIn}
          />
          <IconButton
            icon={iconZoomOut}
            size={48}
            primary={true}
            disabled={zoomLevel == zoomMax}
            onClick={handleZoomOut}
          />
          <IconButton
            onClick={() => {
              copyUrl().then(() =>
                addNotification('URL copied to clipboard', 'success'),
              )
            }}
            icon={iconShare}
            size={48}
            primary={false}
          />
        </MobileToolbar>
      )}

      <ReactTooltip effect="solid" delayShow={300} />
    </div>
  )
}
